// RecipePage.tsx — Full recipe view. Each section is a three-slot Card
// (DESIGN.md C-CARD-1) on a full-bleed page: the short reference cards sit on
// the intrinsic grid, the ingredient table and the procedure run as two
// wrapping tracks, and only the step text keeps a measure (C-LAYOUT-1..3).
// Chrome comes from the shared Shell in App.tsx.
//
// Every mass on this page is an editable `InlineText`, but none of them is a
// field of the recipe: a recipe is one set of ratios, so typing a new mass
// anywhere states what you actually have of that one thing, and the rest of
// the page follows at the same factor. It is a reading aid and lives only as
// long as the view — the recipe is edited on RecipeEditPage, saved, and owned.
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import {
    BsTools, BsRulers, BsListOl, BsJournalText, BsClock,
    BsThermometerHalf, BsArrowLeft, BsPencil,
} from 'react-icons/bs'
import InlineText from 'common/ui/InlineText'
import Callout from '../components/Callout'
import { difficultyBadge } from '../components/RecipeCard'
import { getRecipe, totalMass, type Recipe, type Step, type Ingredient } from '../api'
import { formatGrams, gramsLabel } from '../format'
// A mass may be *typed* in whatever unit the reader has in hand — "2 lb", "1.5
// kg" — but it is only ever *shown* in grams. Recipes here are ratios between
// weighed masses, so one unit across the page is what makes two numbers
// comparable at a glance; the other units exist because a bag says 2 lb on it,
// not because the recipe wants them. Leaving the field converts.
//
// Volume is deliberately absent here, where a typed amount rescales the whole
// recipe: "1 cup" of one ingredient is a ratio only once you know what that cup
// of that food weighs. The editor asks the server, which has the density
// tables; this page keeps to the arithmetic it can do on its own.
import { parseMass } from '../quantity'

// Format seconds into a human-readable duration string
function formatDuration(seconds: number): string {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) {
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return s > 0 ? `${m}m ${s}s` : `${m}m`
    }
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (h >= 24) {
        const d = Math.floor(h / 24)
        const rh = h % 24
        return rh > 0 ? `${d}d ${rh}h` : `${d}d`
    }
    return m > 0 ? `${h}h ${m}m` : `${h}h`
}

// The factor itself, for the badge that says the page is not showing the
// recipe as written.
function formatScale(scale: number): string {
    return String(Math.round(scale * 100) / 100)
}

// A step is a list of things to do, so it is shown as one rather than as a
// paragraph to be re-parsed by whoever is holding the bowl.
//
// The split has to work on recipes already saved, so it is derived rather than
// authored: line breaks where an author put them, and otherwise sentence ends,
// which is where a method's actions already divide. The lookahead is what keeps
// "1.5 tsp", "3-5 times" and "12-inch" whole — a decimal point has no space
// after it, and a sentence end is followed by something that starts one.
function stepLines(instruction: string): string[] {
    const authored = instruction.split(/\r?\n+/).map(s => s.trim()).filter(Boolean)
    if (authored.length > 1) return authored
    return instruction
        .split(/(?<=[.!?])\s+(?=[A-Z"'(])/)
        .map(s => s.trim())
        .filter(Boolean)
}

// MassDraft is the field currently being typed into. It exists because a
// half-typed mass is not a mass: "", "1.", "2 l" on the way to "2 lb" are all
// legal keystrokes, and reformatting them mid-edit fights the cursor. Only the
// focused field shows raw text — every other mass on the page re-renders from
// the scale on each keystroke, which is the point. Dropping the draft on blur
// is also what converts the typed unit to grams: the settled text is always
// rendered from the number.
//
// scaleAtFocus is what Escape puts back.
interface MassDraft {
    key: string
    text: string
    scaleAtFocus: number
}

interface MassFieldProps {
    /** This mass as the recipe records it, before any scaling. */
    baseG: number
    scale: number
    /** Stable identity, so the required/optional split cannot alias two fields. */
    fieldKey: string
    draft: MassDraft | null
    setDraft: (draft: MassDraft | null) => void
    setScale: (scale: number) => void
    /** What this mass is, for screen readers — the value alone says nothing. */
    label: string
}

// MassField renders one mass, and rescales the whole recipe when it is edited.
const MassField = ({
    baseG, scale, fieldKey, draft, setDraft, setScale, label,
}: MassFieldProps) => {
    const editing = draft && draft.key === fieldKey ? draft : null
    const settled = gramsLabel(baseG * scale)
    // An ingredient the parser could not weigh is stored at 0 g. There is no
    // ratio to scale from, so it reads but does not take a value.
    const fixed = baseG <= 0

    return (
        <span className="mass-value text-nowrap">
            <InlineText
                value={editing ? editing.text : settled}
                readOnly={fixed}
                aria-label={label}
                title={fixed
                    ? 'No mass recorded for this ingredient — it still needs weighing.'
                    : 'Type the mass you have — g, kg, oz or lb. The rest of the recipe '
                      + 'scales to match, and the field settles back to grams.'}
                minWidth={5}
                // The dashed border is the affordance that says "type here", so
                // the one field that will not take a value drops it and reads as
                // the plain text it is.
                style={{
                    textAlign: 'right',
                    ...(fixed ? { borderColor: 'transparent', cursor: 'default' } : null),
                }}
                onFocus={() => {
                    if (!fixed) setDraft({ key: fieldKey, text: settled, scaleAtFocus: scale })
                }}
                onChange={e => {
                    const text = e.target.value
                    setDraft({ key: fieldKey, text, scaleAtFocus: editing?.scaleAtFocus ?? scale })
                    const asked = parseMass(text)
                    if (asked !== null && asked > 0) setScale(asked / baseG)
                }}
                onBlur={() => setDraft(null)}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur()
                    } else if (e.key === 'Escape') {
                        if (editing) setScale(editing.scaleAtFocus)
                        setDraft(null)
                        ;(e.target as HTMLInputElement).blur()
                    }
                }}
            />
        </span>
    )
}

const RecipePage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [recipe, setRecipe] = useState<Recipe | null>(null)
    const [loading, setLoading] = useState(true)
    // How far the displayed masses are from the ones the recipe stores. 1 is
    // the recipe as written; anything else is this reader's batch, and it goes
    // away with the page.
    const [scale, setScale] = useState(1)
    const [massDraft, setMassDraft] = useState<MassDraft | null>(null)
    // Which ingredient the reader is pointing at in a step, so its row in the
    // table lights up. The two are the same number in two places, and a recipe
    // is read by looking between them.
    //
    // Two states because a click has to outlive the pointer: going to the row
    // can scroll the page, which takes the chip out from under the cursor and
    // would put the highlight out exactly as the reader arrives at what it was
    // pointing to. Hovering something else still wins over an older click.
    const [hovered, setHovered] = useState<number | null>(null)
    const [picked, setPicked] = useState<number | null>(null)
    const pointedAt = hovered ?? picked

    useEffect(() => {
        if (!id) return
        setLoading(true)
        // A different recipe is a different set of ratios — the previous
        // recipe's factor would be meaningless against it.
        setScale(1)
        setMassDraft(null)
        getRecipe(id)
            .then(setRecipe)
            .catch(() => setRecipe(null))
            .finally(() => setLoading(false))
    }, [id])

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading recipe…</span>
                </Spinner>
            </div>
        )
    }

    if (!recipe) {
        // The card holds one sentence and a button, so it is prose and caps
        // like prose (C-LAYOUT-2) rather than stretching the display.
        return (
            <div className="prose-column">
                <Card>
                    <Card.Header>Recipe Not Found</Card.Header>
                    <Card.Body>
                        <p className="mb-0 text-body-secondary">
                            <code>GET /recipe/{id}</code> returned <code>404 Not Found</code>.
                        </p>
                    </Card.Body>
                    <Card.Footer>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="d-flex align-items-center gap-1"
                            onClick={() => navigate('/')}
                        >
                            <BsArrowLeft /> Back to Recipes
                        </Button>
                    </Card.Footer>
                </Card>
            </div>
        )
    }

    const totalMassG = totalMass(recipe)
    // Carry each ingredient's index in the recipe through the required /
    // optional split: that split is presentation, and a mass field needs an
    // identity that survives it.
    const rows = recipe.ingredients.map((ing, index) => ({ ing, index }))
    const requiredIngredients = rows.filter(r => !r.ing.optional)
    const optionalIngredients = rows.filter(r => r.ing.optional)
    const badge = difficultyBadge[recipe.difficulty]
    const scaled = Math.abs(scale - 1) > 1e-9

    // Shared by every mass on the page, so they all drive the one factor.
    const massField = (fieldKey: string, baseG: number, label: string) => (
        <MassField
            fieldKey={fieldKey}
            baseG={baseG}
            label={label}
            scale={scale}
            draft={massDraft}
            setDraft={setMassDraft}
            setScale={setScale}
        />
    )

    // What a step takes off the ingredient list, paired with the ingredient it
    // names. A use pointing past the end of the list is dropped rather than
    // rendered as a blank chip: the two are saved together, but nothing
    // guarantees the copy in this browser is the newer one.
    const usesOf = (step: Step) =>
        (step.uses ?? [])
            .map(u => ({ use: u, ing: recipe.ingredients[u.ingredient] }))
            .filter(({ ing, use }) => ing !== undefined && use.portion > 0)

    const anyStepAmounts = recipe.steps.some(s => usesOf(s).length > 0)

    // Clicking an ingredient in a step goes to its row, which on a phone is a
    // screen away — the highlight alone would be somewhere the reader is not.
    // On a wide display it usually is not away at all, and scrolling a row that
    // is already in front of you is just the page moving for no reason.
    const showIngredient = (index: number) => {
        setPicked(index)
        const row = document.getElementById(`ingredient-${index}`)
        if (!row) return
        const box = row.getBoundingClientRect()
        if (box.top < 0 || box.bottom > window.innerHeight) {
            row.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
    }

    // An ingredient's note, on the ingredient rather than in a column of its
    // own. A note is usually an account of where the mass came from — "1 lb",
    // "0.5 × 110 g per onion", "sifted" — which is worth having and worth not
    // reading twice: as a column it spent a quarter of the table on every row
    // to say something you want once, and it is the part of the row that cannot
    // be scaled, so at any factor but 1 it sat beside numbers it no longer
    // described. The tooltip says so itself instead.
    const ingredientName = (ing: Ingredient) => {
        const note = ing.notes?.trim()
        if (!note) return ing.name
        return (
            <OverlayTrigger
                placement="top"
                // Rendered at the body, not in the cell it belongs to. Inside a
                // table the bubble is positioned against the cell and stacks
                // under the row's own text — you get the note showing through
                // the ingredient name rather than over it.
                container={document.body}
                overlay={
                    <Tooltip>
                        {note}
                        {scaled && (
                            <div className="fst-italic mt-1">
                                as published — this note is not scaled
                            </div>
                        )}
                    </Tooltip>
                }
            >
                {/* Focusable, because the dotted underline is a hover
                    affordance and hovering is not available to everyone. */}
                <span className="ingredient-note" tabIndex={0}>{ing.name}</span>
            </OverlayTrigger>
        )
    }

    // The mass row that opens a step: what it takes, of what, at this factor.
    // Each amount is the same editable field as the ingredient table's — a
    // recipe is one set of ratios, so saying "I actually poured 60 g" here says
    // exactly what it says there.
    const stepUses = (step: Step) => {
        const uses = usesOf(step)
        if (uses.length === 0) return null
        return (
            <div
                className="step-uses"
                role="group"
                aria-label={`What step ${step.number} uses`}
            >
                {uses.map(({ use, ing }) => (
                    <span
                        key={use.ingredient}
                        className={`step-use${pointedAt === use.ingredient ? ' step-use-pointed' : ''}`}
                        onMouseEnter={() => setHovered(use.ingredient)}
                        onMouseLeave={() => setHovered(null)}
                    >
                        <button
                            type="button"
                            className="step-use-name"
                            onClick={() => showIngredient(use.ingredient)}
                            onFocus={() => setHovered(use.ingredient)}
                            onBlur={() => setHovered(null)}
                            title={`${formatGrams(ing.massG * use.portion * scale)} g of the `
                                + `${formatGrams(ing.massG * scale)} g of ${ing.name} this recipe uses`
                                + ' — go to its row'}
                        >
                            {ing.name}
                        </button>
                        {massField(
                            `step-${step.number}-use-${use.ingredient}`,
                            ing.massG * use.portion,
                            `${ing.name} used in step ${step.number}`,
                        )}
                    </span>
                ))}
            </div>
        )
    }

    return (
        <div>
            {/* The trail names the recipe, not its URL segment, and runs at
                default size (DESIGN.md C-NAV-2). */}
            <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>Recipes</Breadcrumb.Item>
                <Breadcrumb.Item active>{recipe.title}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
                <div>
                    <h1 className="fs-3 mb-1">{recipe.title}</h1>
                    <p className="text-body-secondary mb-0">{recipe.subtitle}</p>
                </div>
                {/* Owner-only. The server re-checks on save, so this is a UX
                    affordance rather than the access control itself. */}
                {recipe.canEdit && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="d-flex align-items-center gap-2"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                    >
                        <BsPencil /> Edit
                    </Button>
                )}
            </div>

            {/* The two short reference cards are peers, so they go on the
                intrinsic grid (C-LAYOUT-3): side by side wherever there is
                room for both, stacked on a phone, with no breakpoint to keep
                in sync. */}
            <div className="card-grid mb-4">
                {/* Metadata — the field-rows table pattern from DESIGN.md Appendix A. */}
                <Card>
                    <Card.Header>Recipe Metadata</Card.Header>
                    <Card.Body>
                        <Table size="sm" borderless className="m-0">
                            <tbody>
                                <tr>
                                    <td className="text-body-secondary" style={{ width: '25%' }}>Category</td>
                                    <td className="fw-bold">{recipe.category}</td>
                                    <td className="text-body-secondary" style={{ width: '25%' }}>Yield</td>
                                    <td className="fw-bold">
                                        {recipe.yieldDescription}
                                        {/* The yield is prose — "12 rolls" — so it
                                            cannot be scaled, only qualified. */}
                                        {scaled && (
                                            <span className="fw-normal text-body-secondary">
                                                {' '}× {formatScale(scale)}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-body-secondary">Total Time</td>
                                    <td className="fw-bold">{formatDuration(recipe.totalTimeMin * 60)}</td>
                                    <td className="text-body-secondary">Total Mass</td>
                                    <td className="fw-bold">
                                        {massField('total-meta', totalMassG, 'Total mass')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-body-secondary">Prep Time</td>
                                    <td className="fw-bold">{formatDuration(recipe.prepTimeMin * 60)}</td>
                                    <td className="text-body-secondary">Cook Time</td>
                                    <td className="fw-bold">{formatDuration(recipe.cookTimeMin * 60)}</td>
                                </tr>
                                <tr>
                                    <td className="text-body-secondary">Steps</td>
                                    <td className="fw-bold">{recipe.steps.length}</td>
                                    <td className="text-body-secondary">Ingredients</td>
                                    <td className="fw-bold">{recipe.ingredients.length}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-end align-items-center gap-2">
                        <Badge bg={badge.bg} text={badge.text} className="text-uppercase">
                            {recipe.difficulty}
                        </Badge>
                        <Badge bg="secondary">v{recipe.version}</Badge>
                    </Card.Footer>
                </Card>

                {/* Prerequisites */}
                <Card>
                    <Card.Header className="d-flex align-items-center gap-2">
                        <BsTools /> Prerequisites
                    </Card.Header>
                    <Card.Body>
                        <p className="mb-2 prose-column">Ensure the following equipment is available before beginning:</p>
                        <ul className="mb-0">
                            {recipe.equipment.map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </Card.Body>
                    <Card.Footer className="text-end small text-body-secondary">
                        {recipe.equipment.length} items required
                    </Card.Footer>
                </Card>
            </div>

            {/* The working pair. On a wide display the masses stay in view
                beside the step you are reading, which is the whole reason a
                recipe wants the width; below that the two stack and the
                procedure keeps its measure either way. */}
            <div className="recipe-workspace">
                <section className="recipe-ingredients">
                    {/* Ingredients */}
                    <Card>
                        <Card.Header className="d-flex align-items-center gap-2">
                            <BsRulers /> Ingredients
                            {scaled && (
                                <Badge bg="secondary" className="ms-auto" title="These are not the masses as written">
                                    × {formatScale(scale)}
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <p className="small text-body-secondary mb-2 prose-column">
                                Type over any mass to work from what you actually have — every
                                other mass follows at the same ratio. Grams, kilograms, ounces
                                and pounds are all understood, and settle back to grams when you
                                leave the field. Nothing here is saved.
                            </p>
                            <Table size="sm" hover responsive className="ingredient-table m-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>#</th>
                                        <th>Ingredient</th>
                                        <th style={{ width: '120px' }} className="text-end">Mass</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requiredIngredients.map(({ ing, index }, i) => (
                                        <tr
                                            key={index}
                                            id={`ingredient-${index}`}
                                            className={pointedAt === index ? 'ingredient-pointed' : undefined}
                                        >
                                            <td className="text-body-secondary">{i + 1}</td>
                                            <td>{ingredientName(ing)}</td>
                                            <td className="text-end">
                                                {massField(`ing-${index}`, ing.massG, `Mass of ${ing.name}`)}
                                            </td>
                                        </tr>
                                    ))}
                                    {optionalIngredients.length > 0 && (
                                        <>
                                            <tr>
                                                <td colSpan={3} className="text-body-secondary small fst-italic">
                                                    Optional — may be omitted without affecting the core result
                                                </td>
                                            </tr>
                                            {optionalIngredients.map(({ ing, index }, i) => (
                                                <tr
                                                    key={index}
                                                    id={`ingredient-${index}`}
                                                    className={'text-body-secondary'
                                                        + (pointedAt === index ? ' ingredient-pointed' : '')}
                                                >
                                                    <td>{requiredIngredients.length + i + 1}</td>
                                                    <td className="fst-italic">{ingredientName(ing)}</td>
                                                    <td className="text-end">
                                                        {massField(`ing-${index}`, ing.massG, `Mass of ${ing.name}`)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                        <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
                            <span className="small text-body-secondary">Total mass</span>
                            <span className="d-flex align-items-center gap-3">
                                {scaled && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 small"
                                        onClick={() => { setScale(1); setMassDraft(null) }}
                                    >
                                        Reset to {formatGrams(totalMassG)} g
                                    </Button>
                                )}
                                {massField('total-footer', totalMassG, 'Total mass')}
                            </span>
                        </Card.Footer>
                    </Card>
                </section>

                <section className="recipe-procedure">
                    {/* Procedure */}
                    <Card className="mb-4">
                        <Card.Header className="d-flex align-items-center gap-2">
                            <BsListOl /> Procedure
                        </Card.Header>
                        <Card.Body>
                            {anyStepAmounts ? (
                                <p className="small text-body-secondary mb-3 prose-column">
                                    Each step opens with what it takes off the ingredient list.
                                    Those amounts are editable too: weigh out what you actually
                                    have — or what you have already poured — and the whole
                                    recipe follows from it.
                                </p>
                            ) : recipe.canEdit && recipe.ingredients.length > 0 && (
                                // Only to the owner, and only as a route to the one
                                // place that can fix it. A reader who cannot act on it
                                // is being told about an empty field.
                                <p className="small text-body-secondary mb-3 prose-column">
                                    This recipe records no per-step amounts, so the steps read as
                                    published and do not scale.{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 align-baseline"
                                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                                    >
                                        Work them out in the editor
                                    </Button>.
                                </p>
                            )}
                            <div className="procedure-list">
                                {recipe.steps.map(step => (
                                    <div key={step.number} className="procedure-step">
                                        <div className="step-header">
                                            <span className="step-number">
                                                {String(step.number).padStart(2, '0')}
                                            </span>
                                            <Stack direction="horizontal" gap={2} className="step-badges ms-auto">
                                                {step.durationSec && (
                                                    <Badge bg="secondary" title="Duration">
                                                        <BsClock className="me-1" />
                                                        {formatDuration(step.durationSec)}
                                                    </Badge>
                                                )}
                                                {/* A temperature is a measurement, not an error state — it
                                                    keeps the neutral variant and leans on its icon, rather
                                                    than repurposing `danger` for non-state content. */}
                                                {step.temperatureC && (
                                                    <Badge bg="secondary" title="Temperature">
                                                        <BsThermometerHalf className="me-1" />
                                                        {step.temperatureC}°C
                                                    </Badge>
                                                )}
                                            </Stack>
                                        </div>
                                        {/* What the step takes, before what it does
                                            with it: the amounts are what you weigh out
                                            first, and they are the part of the step
                                            that follows the factor. A mass written into
                                            the sentence below cannot. */}
                                        {stepUses(step)}
                                        {/* Not column-capped, and deliberately: the
                                            measure is for running text, and C-LAYOUT-2
                                            exempts what is not prose — "a card of
                                            key/value rows, a table, and a grid of
                                            tiles". A list of actions to work down is
                                            the same kind of thing, and capping it at
                                            68ch only makes a long method taller. */}
                                        <ul className="step-instruction">
                                            {stepLines(step.instruction).map((line, n) => (
                                                <li key={n}>{line}</li>
                                            ))}
                                        </ul>
                                        {step.callout && (
                                            <Callout variant={step.callout.variant}>
                                                {step.callout.text}
                                            </Callout>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                        <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
                            {/* A mass written into an instruction is still prose the
                                page cannot rescale, so at any factor but 1 a sentence
                                and the amounts above it disagree. Say which one moved
                                rather than let it be found mid-bake. */}
                            <span className="small text-body-secondary">
                                {!scaled
                                    ? `${recipe.steps.length} steps`
                                    : anyStepAmounts
                                        ? 'The amounts above each step are scaled; masses written into the '
                                          + 'sentences are as published.'
                                        : 'Masses written into the steps are as published — they are not scaled.'}
                            </span>
                            <span className="small text-body-secondary text-nowrap">
                                {formatDuration(recipe.totalTimeMin * 60)} total
                            </span>
                        </Card.Footer>
                    </Card>

                    {/* Additional Notes — they annotate the procedure, so they travel
                        with it rather than starting a third track. */}
                    {recipe.notes.length > 0 && (
                        <Card>
                            <Card.Header className="d-flex align-items-center gap-2">
                                <BsJournalText /> Additional Notes
                            </Card.Header>
                            <Card.Body>
                                {recipe.notes.map((note, i) => (
                                    <Callout key={i} variant={note.variant}>
                                        {note.text}
                                    </Callout>
                                ))}
                            </Card.Body>
                            <Card.Footer className="text-end small text-body-secondary">
                                {recipe.notes.length} notes
                            </Card.Footer>
                        </Card>
                    )}
                </section>
            </div>

            <footer className="mt-5 pt-4 border-top text-center text-body-secondary small">
                <p className="mb-0">{recipe.title} v{recipe.version}</p>
            </footer>
        </div>
    )
}

export default RecipePage
